export interface LegalListItem {
  label?: string;
  text: string;
}

export type LegalBlock =
  | { type: "lead"; text: string }
  | { type: "h2"; id: string; text: string }
  | { type: "p"; text: string; label?: string }
  | { type: "ul"; items: LegalListItem[] }
  | { type: "cookieButton"; text: string }
  | { type: "contact" };

export interface LegalDocument {
  title: string;
  lastUpdated: string;
  tocTitle: string;
  metaTitle: string;
  metaDescription: string;
  canonical: string;
  sections: { id: string; title: string }[];
  blocks: LegalBlock[];
}

/* ------------------------------------------------------------------ */
/* PRIVACY – Hebrew                                                    */
/* ------------------------------------------------------------------ */

const privacyHe: LegalDocument = {
  title: "מדיניות פרטיות",
  lastUpdated: "עדכון אחרון: יולי 2026",
  tocTitle: "תוכן עניינים",
  metaTitle: "מדיניות פרטיות | אדם בטיחות",
  metaDescription:
    "מדיניות הפרטיות של אדם בטיחות – איסוף מידע, שימוש, אבטחה, דיוור וזכויותיכם בהתאם לחוק הגנת הפרטיות התשמ״א-1981 ולדין הישראלי.",
  canonical: "https://adam-safety.com/privacy",
  sections: [
    { id: "general", title: "1. כללי ותחולה" },
    { id: "consent", title: "2. מסירת מידע מרצון והסכמה" },
    { id: "collected", title: "3. סוגי המידע הנאסף" },
    { id: "purposes", title: "4. מטרות השימוש במידע" },
    { id: "database", title: "5. שמירת המידע ומאגר מידע" },
    { id: "marketing", title: "6. דיוור שיווקי (סעיף 30א)" },
    { id: "sharing", title: "7. מסירת מידע לצדדים שלישיים" },
    { id: "transfer", title: "8. העברת מידע אל מחוץ לישראל" },
    { id: "security", title: "9. אבטחת מידע" },
    { id: "retention", title: "10. תקופות שמירת מידע" },
    { id: "rights", title: "11. זכויותיכם על פי חוק" },
    { id: "minors", title: "12. קטינים" },
    { id: "cookies", title: "13. עוגיות (Cookies)" },
    { id: "links", title: "14. קישורים לאתרים חיצוניים" },
    { id: "changes", title: "15. שינויים במדיניות" },
    { id: "law", title: "16. דין וסמכות שיפוט" },
    { id: "contact", title: "17. יצירת קשר" },
  ],
  blocks: [
    {
      type: "lead",
      text: 'אנו ב"אדם בטיחות" (להלן: "החברה", "אנחנו") מכבדים את פרטיותכם ומחויבים להגן על המידע האישי שנמסר לנו, בהתאם לחוק הגנת הפרטיות, התשמ"א-1981 ולתקנות שהותקנו מכוחו, לרבות תקנות הגנת הפרטיות (אבטחת מידע), התשע"ז-2017.',
    },
    { type: "h2", id: "general", text: "1. כללי ותחולה" },
    {
      type: "p",
      text: 'מדיניות פרטיות זו מתארת כיצד אנו אוספים, משתמשים, שומרים ומגנים על מידע אישי הנאסף באמצעות אתר האינטרנט שלנו, טפסי יצירת הקשר, מערכת קביעת הפגישות, ערוצי התקשורת שלנו (דוא"ל, טלפון ווואטסאפ) וכן במסגרת מתן שירותי הבטיחות, הייעוץ וההדרכה שלנו.',
    },
    {
      type: "p",
      text: "המדיניות מנוסחת בלשון זכר מטעמי נוחות בלבד ומתייחסת לכל המגדרים. גלישה באתר ושימוש בשירותיו מהווים הסכמה לתנאי מדיניות זו. אם אינכם מסכימים לתנאים, אנא הימנעו משימוש באתר וממסירת מידע.",
    },
    { type: "h2", id: "consent", text: "2. מסירת מידע מרצון והסכמה" },
    {
      type: "p",
      text: "אינכם חייבים על פי דין למסור לנו מידע אישי. מסירת המידע נעשית מרצונכם החופשי ובהסכמתכם, לצורך קבלת השירותים או המענה שביקשתם. עם זאת, ללא מסירת פרטים מסוימים (כגון שם ודרך התקשרות) לא נוכל לטפל בפנייתכם או לספק את השירות.",
    },
    {
      type: "p",
      text: "בעת מסירת פרטי צד שלישי (למשל עובד או איש קשר בארגון), אתם מצהירים כי קיבלתם את הסכמתו למסירת פרטיו לשימוש המתואר במדיניות זו.",
    },
    { type: "h2", id: "collected", text: "3. סוגי המידע הנאסף" },
    {
      type: "p",
      label: "מידע שנמסר על ידכם:",
      text: 'שם מלא, מספר טלפון, כתובת דוא"ל, שם הארגון ותפקיד, תחום הפעילות, פרטי הפנייה או הצורך המקצועי, וכל מידע נוסף שתבחרו למסור בטופס, בשיחה או בהתכתבות.',
    },
    {
      type: "p",
      label: "מידע הנאסף אוטומטית:",
      text: "נתוני שימוש טכניים כגון כתובת IP, סוג מכשיר ודפדפן, מערכת הפעלה, שפת ממשק, עמודים שנצפו, משך השהייה ומקור ההפניה לאתר. מידע זה נאסף בעיקר בצורה סטטיסטית ומצטברת לצורך תפעול ושיפור האתר.",
    },
    {
      type: "p",
      label: "מידע מהתקשרות ותיאום:",
      text: 'תוכן הודעות שנשלחו אלינו בוואטסאפ או בדוא"ל, ופרטי מועד שנקבע באמצעות מערכת קביעת הפגישות המשולבת באתר.',
    },
    {
      type: "p",
      text: "אנו נמנעים מאיסוף מידע רגיש שאינו נדרש. אין למסור לנו מידע רפואי, פלילי או מידע רגיש אחר אלא אם הדבר נדרש במפורש לצורך מתן השירות (למשל בחקירת תאונת עבודה), ובמקרה כזה המידע ישמש אך ורק למטרה זו.",
    },
    { type: "h2", id: "purposes", text: "4. מטרות השימוש במידע" },
    {
      type: "ul",
      items: [
        { text: "מענה לפניות, מתן הצעות מחיר ותיאום פגישות" },
        { text: "אספקת שירותי בטיחות, ייעוץ, הדרכה וסקרי סיכונים וניהולם השוטף" },
        { text: "ניהול קשרי לקוחות, חיוב, גבייה והנהלת חשבונות" },
        {
          text: "עמידה בחובות רגולטוריות ותיעודיות החלות עלינו ועל לקוחותינו לפי דיני הבטיחות בעבודה",
        },
        { text: "שיפור האתר, השירותים והתכנים באמצעות ניתוח שימוש מצטבר" },
        { text: "משלוח עדכונים ותכנים מקצועיים, בכפוף להסכמתכם כמפורט בסעיף 6" },
        { text: "הגנה על זכויותינו החוקיות ומניעת שימוש לרעה או הונאה" },
      ],
    },
    { type: "h2", id: "database", text: "5. שמירת המידע ומאגר מידע" },
    {
      type: "p",
      text: "המידע שנאסף נשמר במערכות מאובטחות ומעובד בהתאם להוראות חוק הגנת הפרטיות ותקנות אבטחת מידע. ככל שחלה עלינו חובת רישום מאגר מידע לפי הדין, אנו פועלים לקיומה. השימוש במידע ייעשה אך ורק למטרות המפורטות במדיניות זו או למטרה שלשמה נמסר.",
    },
    { type: "h2", id: "marketing", text: "6. דיוור שיווקי (סעיף 30א לחוק התקשורת)" },
    {
      type: "p",
      text: 'אנו עשויים לשלוח לכם דברי פרסומת, עדכונים מקצועיים וניוזלטר בדוא"ל, בהודעות טקסט או בוואטסאפ – רק לאחר קבלת הסכמתכם המפורשת מראש ובכתב, או בהתאם לחריגים הקבועים בסעיף 30א(ג) לחוק התקשורת (בזק ושידורים), התשמ"ב-1982, לגבי לקוחות קיימים.',
    },
    {
      type: "p",
      text: "בכל הודעה כאמור תופיע אפשרות פשוטה וללא עלות להסרה מרשימת התפוצה. תוכלו גם להודיע לנו על סירובכם בכל עת בדוא\"ל Support@Adam-Safety.com, ובקשתכם תיושם בהקדם וללא תנאי. הסרה מדיוור שיווקי אינה מונעת מאיתנו לשלוח לכם הודעות תפעוליות הנוגעות לשירות שהזמנתם.",
    },
    { type: "h2", id: "sharing", text: "7. מסירת מידע לצדדים שלישיים" },
    {
      type: "p",
      text: "איננו מוכרים, משכירים או סוחרים במידע האישי שלכם. מידע עשוי להימסר לצדדים שלישיים במקרים הבאים בלבד:",
    },
    {
      type: "ul",
      items: [
        {
          label: "ספקי שירות ותשתית",
          text: "הפועלים מטעמנו ובהתאם להנחיותינו – אחסון ענן ובסיסי נתונים, מערכת דיוור, מערכת קביעת פגישות (Cal.com), שירותי תקשורת (וואטסאפ) ושירותי ניתוח שימוש. ספקים אלה מחויבים לשמור על סודיות ולעשות שימוש במידע לצורך מתן השירות בלבד.",
        },
        {
          label: "יועצים מקצועיים",
          text: '(רו"ח, עו"ד) ככל שנדרש לצורך ניהול העסק והגנה על זכויותינו.',
        },
        {
          label: "רשויות מוסמכות",
          text: "– בהתאם לצו שיפוטי, דרישת רשות מוסמכת או חובה חוקית, וכן לצורך מניעת פגיעה או הגנה על זכויות משפטיות.",
        },
        {
          label: "שינוי מבני",
          text: "– במקרה של מיזוג, מכירה או העברת פעילות, בכפוף לכך שהגורם הנעבר יקבל על עצמו את הוראות מדיניות זו.",
        },
      ],
    },
    { type: "h2", id: "transfer", text: "8. העברת מידע אל מחוץ לישראל" },
    {
      type: "p",
      text: 'חלק מספקי התשתית והשירותים שלנו מאחסנים או מעבדים מידע בשרתים הממוקמים מחוץ לישראל. העברה כאמור תיעשה בכפוף לתקנות הגנת הפרטיות (העברת מידע למאגרי מידע שמחוץ לגבולות המדינה), התשס"א-2001, ולמדינות או ספקים המבטיחים רמת הגנה הולמת על המידע.',
    },
    { type: "h2", id: "security", text: "9. אבטחת מידע" },
    {
      type: "p",
      text: "אנו נוקטים אמצעי אבטחה סבירים ומקובלים להגנה על המידע, ובכללם הצפנת תעבורה (HTTPS/TLS), ניהול הרשאות גישה לפי הצורך, מנגנוני הזדהות, גיבויים תקופתיים ובקרה על ספקים. אנו פועלים בהתאם לרמת האבטחה הנדרשת לפי תקנות אבטחת מידע.",
    },
    {
      type: "p",
      text: "יחד עם זאת, אין באפשרותנו להבטיח חסינות מוחלטת מפני חדירה בלתי מורשית או שימוש לרעה במידע. השימוש באתר נעשה על אחריותכם, ובכפוף לכך לא תהיה לכם טענה או תביעה כלפינו בגין נזק שנגרם כתוצאה מאירוע כאמור, למעט במקרים שבהם הדין קובע אחרת.",
    },
    { type: "h2", id: "retention", text: "10. תקופות שמירת מידע" },
    {
      type: "p",
      text: "אנו שומרים מידע אישי כל עוד הוא נדרש למטרות שלשמן נאסף, לרבות לצורך קיום יחסי לקוח שוטפים. לאחר מכן יישמר המידע רק ככל שנדרש לפי דין (למשל חובות תיעוד ומיסוי) או לצורך הגנה מפני תביעות עד לתום תקופת ההתיישנות, ולאחר מכן יימחק או יעבור אנונימיזציה.",
    },
    { type: "h2", id: "rights", text: "11. זכויותיכם על פי חוק" },
    {
      type: "ul",
      items: [
        {
          label: "זכות עיון",
          text: "– לפי סעיף 13 לחוק הגנת הפרטיות, אתם רשאים לעיין במידע אודותיכם המוחזק אצלנו.",
        },
        {
          label: "זכות תיקון או מחיקה",
          text: "– לפי סעיף 14 לחוק, אם המידע אינו נכון, שלם, ברור או מעודכן, אתם רשאים לבקש את תיקונו או מחיקתו.",
        },
        { label: "ביטול הסכמה לדיוור", text: "– בכל עת וללא עלות, כמפורט בסעיף 6." },
        {
          label: "פנייה לערכאות",
          text: "– אם סורבה בקשתכם, עומדת לכם הזכות לפנות לבית משפט השלום בהתאם לחוק.",
        },
      ],
    },
    {
      type: "p",
      text: "בקשות בעניין זה יש להפנות לכתובת Support@Adam-Safety.com. נשיב לפנייתכם בתוך פרק זמן סביר ובהתאם למועדים הקבועים בדין. לצורך הגנה על פרטיותכם, אנו עשויים לבקש אימות זהות טרם הטיפול בבקשה.",
    },
    { type: "h2", id: "minors", text: "12. קטינים" },
    {
      type: "p",
      text: "האתר והשירותים מיועדים לגורמים עסקיים ולבגירים. איננו אוספים ביודעין מידע אישי מקטינים מתחת לגיל 16. אם נודע לכם שקטין מסר לנו מידע, אנא פנו אלינו ונפעל למחיקתו.",
    },
    { type: "h2", id: "cookies", text: "13. עוגיות (Cookies)" },
    {
      type: "p",
      text: "האתר עושה שימוש בעוגיות ובטכנולוגיות דומות. עוגיות הכרחיות נדרשות לתפעול תקין של האתר ולשמירת העדפותיכם (לרבות בחירת השפה והחלטתכם בנוגע לעוגיות). עוגיות סטטיסטיקה וביצועים משמשות להבנת אופן השימוש באתר ולשיפורו.",
    },
    {
      type: "p",
      text: 'בכניסה הראשונה לאתר מוצגת הודעה המאפשרת לאשר או לדחות שימוש בעוגיות שאינן הכרחיות. הבחירה נשמרת בדפדפן שלכם וניתן לשנותה בכל עת באמצעות הקישור "הגדרות עוגיות" שבתחתית האתר או דרך הגדרות הדפדפן. חסימת עוגיות עשויה לפגוע בחלק מהפונקציונליות של האתר.',
    },
    { type: "cookieButton", text: "פתיחת הגדרות העוגיות" },
    { type: "h2", id: "links", text: "14. קישורים לאתרים חיצוניים" },
    {
      type: "p",
      text: "האתר עשוי לכלול קישורים לאתרים ולשירותים של צדדים שלישיים. איננו אחראים לתוכנם, לזמינותם או למדיניות הפרטיות שלהם, ומומלץ לעיין בתנאי השימוש ובמדיניות הפרטיות של כל אתר בנפרד.",
    },
    { type: "h2", id: "changes", text: "15. שינויים במדיניות" },
    {
      type: "p",
      text: "אנו רשאים לעדכן מדיניות זו מעת לעת, בין היתר בשל שינויי חקיקה או שינויים בשירותים. הנוסח המחייב הוא הנוסח המפורסם באתר, ותוקפו ממועד פרסומו. מומלץ לשוב ולעיין בעמוד זה מפעם לפעם.",
    },
    { type: "h2", id: "law", text: "16. דין וסמכות שיפוט" },
    {
      type: "p",
      text: "על מדיניות זו ועל כל שימוש באתר יחולו דיני מדינת ישראל בלבד. סמכות השיפוט הייחודית בכל מחלוקת הנוגעת למדיניות זו נתונה לבתי המשפט המוסמכים במחוז תל אביב-יפו.",
    },
    { type: "h2", id: "contact", text: "17. יצירת קשר" },
    {
      type: "p",
      text: "לשאלות, בקשות עיון, תיקון או מחיקת מידע, או בכל עניין הנוגע למדיניות פרטיות זו, ניתן לפנות אלינו:",
    },
    { type: "contact" },
  ],
};

/* ------------------------------------------------------------------ */
/* PRIVACY – English                                                   */
/* ------------------------------------------------------------------ */

const privacyEn: LegalDocument = {
  title: "Privacy Policy",
  lastUpdated: "Last updated: July 2026",
  tocTitle: "Table of contents",
  metaTitle: "Privacy Policy | Adam Safety",
  metaDescription:
    "Adam Safety's privacy policy – data collection, use, security, marketing communications and your rights under the Israeli Protection of Privacy Law, 5741-1981.",
  canonical: "https://adam-safety.com/privacy",
  sections: [
    { id: "general", title: "1. General and scope" },
    { id: "consent", title: "2. Voluntary disclosure and consent" },
    { id: "collected", title: "3. Types of information collected" },
    { id: "purposes", title: "4. Purposes of use" },
    { id: "database", title: "5. Data storage and database" },
    { id: "marketing", title: "6. Marketing communications (Section 30A)" },
    { id: "sharing", title: "7. Sharing with third parties" },
    { id: "transfer", title: "8. Transfers outside Israel" },
    { id: "security", title: "9. Information security" },
    { id: "retention", title: "10. Retention periods" },
    { id: "rights", title: "11. Your statutory rights" },
    { id: "minors", title: "12. Minors" },
    { id: "cookies", title: "13. Cookies" },
    { id: "links", title: "14. External links" },
    { id: "changes", title: "15. Changes to this policy" },
    { id: "law", title: "16. Governing law and jurisdiction" },
    { id: "contact", title: "17. Contact us" },
  ],
  blocks: [
    {
      type: "lead",
      text: 'At "Adam Safety" (the "Company", "we") we respect your privacy and are committed to protecting the personal information you provide to us, in accordance with the Israeli Protection of Privacy Law, 5741-1981 and the regulations enacted thereunder, including the Protection of Privacy (Data Security) Regulations, 5777-2017.',
    },
    { type: "h2", id: "general", text: "1. General and scope" },
    {
      type: "p",
      text: "This privacy policy describes how we collect, use, store and protect personal information gathered through our website, contact forms, appointment booking system, communication channels (email, phone and WhatsApp), and in the course of providing our safety, consulting and training services.",
    },
    {
      type: "p",
      text: "Browsing the site and using its services constitutes your agreement to this policy. If you do not agree to these terms, please refrain from using the site and from submitting information.",
    },
    { type: "h2", id: "consent", text: "2. Voluntary disclosure and consent" },
    {
      type: "p",
      text: "You are under no legal obligation to provide us with personal information. Any information is provided of your own free will and with your consent, in order to receive the services or the response you requested. However, without certain details (such as a name and contact method) we will not be able to handle your enquiry or deliver the service.",
    },
    {
      type: "p",
      text: "When providing details of a third party (for example an employee or an organisational contact), you represent that you have obtained their consent to the use described in this policy.",
    },
    { type: "h2", id: "collected", text: "3. Types of information collected" },
    {
      type: "p",
      label: "Information you provide:",
      text: "full name, phone number, email address, organisation name and role, field of activity, the details of your enquiry or professional need, and any additional information you choose to share in a form, call or correspondence.",
    },
    {
      type: "p",
      label: "Information collected automatically:",
      text: "technical usage data such as IP address, device and browser type, operating system, interface language, pages viewed, time on site and referral source. This information is used mainly in aggregated, statistical form to operate and improve the site.",
    },
    {
      type: "p",
      label: "Communication and scheduling data:",
      text: "the content of messages sent to us via WhatsApp or email, and the details of appointments booked through the scheduling system embedded in the site.",
    },
    {
      type: "p",
      text: "We avoid collecting sensitive information that is not required. Please do not send us medical, criminal or other sensitive information unless it is expressly required to deliver the service (for example, in a workplace accident investigation), in which case it will be used solely for that purpose.",
    },
    { type: "h2", id: "purposes", text: "4. Purposes of use" },
    {
      type: "ul",
      items: [
        { text: "Responding to enquiries, providing quotes and scheduling meetings" },
        {
          text: "Delivering and managing safety services, consulting, training and risk surveys",
        },
        { text: "Customer relationship management, billing, collection and accounting" },
        {
          text: "Meeting regulatory and documentation obligations applicable to us and to our clients under occupational safety law",
        },
        { text: "Improving the site, services and content through aggregated usage analysis" },
        {
          text: "Sending professional updates and content, subject to your consent as detailed in Section 6",
        },
        { text: "Protecting our legal rights and preventing misuse or fraud" },
      ],
    },
    { type: "h2", id: "database", text: "5. Data storage and database" },
    {
      type: "p",
      text: "Collected information is stored in secured systems and processed in accordance with the Protection of Privacy Law and the Data Security Regulations. Where a database registration obligation applies to us under law, we act to comply with it. Information will be used solely for the purposes set out in this policy or for the purpose for which it was provided.",
    },
    {
      type: "h2",
      id: "marketing",
      text: "6. Marketing communications (Section 30A of the Communications Law)",
    },
    {
      type: "p",
      text: "We may send you advertising material, professional updates and newsletters by email, text message or WhatsApp – only after receiving your explicit prior written consent, or in accordance with the exceptions set out in Section 30A(c) of the Communications (Telecommunications and Broadcasts) Law, 5742-1982 regarding existing customers.",
    },
    {
      type: "p",
      text: "Every such message will include a simple, free-of-charge option to unsubscribe. You may also notify us of your refusal at any time at Support@Adam-Safety.com, and your request will be honoured promptly and unconditionally. Unsubscribing from marketing does not prevent us from sending operational messages relating to a service you ordered.",
    },
    { type: "h2", id: "sharing", text: "7. Sharing with third parties" },
    {
      type: "p",
      text: "We do not sell, rent or trade your personal information. Information may be shared with third parties only in the following cases:",
    },
    {
      type: "ul",
      items: [
        {
          label: "Service and infrastructure providers",
          text: "acting on our behalf and under our instructions – cloud hosting and databases, mailing platform, appointment booking (Cal.com), communication services (WhatsApp) and usage analytics. These providers are bound to confidentiality and may use the information solely to provide the service.",
        },
        {
          label: "Professional advisers",
          text: "(accountants, lawyers) as required to manage the business and protect our rights.",
        },
        {
          label: "Competent authorities",
          text: "– pursuant to a court order, a lawful demand by an authority or a legal obligation, and to prevent harm or defend legal rights.",
        },
        {
          label: "Corporate change",
          text: "– in the event of a merger, sale or transfer of operations, provided the transferee assumes the obligations of this policy.",
        },
      ],
    },
    { type: "h2", id: "transfer", text: "8. Transfers outside Israel" },
    {
      type: "p",
      text: "Some of our infrastructure and service providers store or process information on servers located outside Israel. Any such transfer is carried out subject to the Protection of Privacy (Transfer of Data to Databases Abroad) Regulations, 5761-2001, and to countries or providers ensuring an adequate level of data protection.",
    },
    { type: "h2", id: "security", text: "9. Information security" },
    {
      type: "p",
      text: "We apply reasonable and accepted security measures to protect the information, including traffic encryption (HTTPS/TLS), need-to-know access controls, authentication mechanisms, periodic backups and vendor oversight, in line with the security level required by the Data Security Regulations.",
    },
    {
      type: "p",
      text: "Nevertheless, we cannot guarantee absolute immunity from unauthorised access or misuse. Use of the site is at your own risk and, subject to applicable law, you will have no claim against us for damage resulting from such an event.",
    },
    { type: "h2", id: "retention", text: "10. Retention periods" },
    {
      type: "p",
      text: "We retain personal information for as long as it is required for the purposes for which it was collected, including maintaining an ongoing client relationship. Thereafter it is kept only as required by law (for example documentation and tax obligations) or to defend against claims until the end of the limitation period, after which it is deleted or anonymised.",
    },
    { type: "h2", id: "rights", text: "11. Your statutory rights" },
    {
      type: "ul",
      items: [
        {
          label: "Right of access",
          text: "– under Section 13 of the Protection of Privacy Law, you may review the information we hold about you.",
        },
        {
          label: "Right to correction or deletion",
          text: "– under Section 14 of the Law, if the information is inaccurate, incomplete, unclear or out of date, you may request its correction or deletion.",
        },
        {
          label: "Withdrawal of marketing consent",
          text: "– at any time and free of charge, as detailed in Section 6.",
        },
        {
          label: "Recourse to the courts",
          text: "– if your request is refused, you are entitled to apply to the Magistrates' Court as provided by law.",
        },
      ],
    },
    {
      type: "p",
      text: "Such requests should be addressed to Support@Adam-Safety.com. We will respond within a reasonable time and within the periods set by law. To protect your privacy, we may request identity verification before handling a request.",
    },
    { type: "h2", id: "minors", text: "12. Minors" },
    {
      type: "p",
      text: "The site and services are intended for business entities and adults. We do not knowingly collect personal information from minors under the age of 16. If you become aware that a minor has provided us with information, please contact us and we will act to delete it.",
    },
    { type: "h2", id: "cookies", text: "13. Cookies" },
    {
      type: "p",
      text: "The site uses cookies and similar technologies. Essential cookies are required for the proper operation of the site and to store your preferences (including language selection and your cookie choice). Statistics and performance cookies help us understand how the site is used and improve it.",
    },
    {
      type: "p",
      text: 'On your first visit a banner is displayed allowing you to accept or decline non-essential cookies. Your choice is stored in your browser and can be changed at any time via the "Cookie settings" link in the site footer or through your browser settings. Blocking cookies may impair some site functionality.',
    },
    { type: "cookieButton", text: "Open cookie settings" },
    { type: "h2", id: "links", text: "14. External links" },
    {
      type: "p",
      text: "The site may include links to third-party websites and services. We are not responsible for their content, availability or privacy practices, and we recommend reviewing the terms and privacy policy of each site separately.",
    },
    { type: "h2", id: "changes", text: "15. Changes to this policy" },
    {
      type: "p",
      text: "We may update this policy from time to time, including due to legislative changes or changes to our services. The binding version is the one published on the site, effective from the date of its publication. We recommend revisiting this page periodically.",
    },
    { type: "h2", id: "law", text: "16. Governing law and jurisdiction" },
    {
      type: "p",
      text: "This policy and any use of the site are governed exclusively by the laws of the State of Israel. Exclusive jurisdiction over any dispute relating to this policy is granted to the competent courts of the Tel Aviv-Jaffa district.",
    },
    { type: "h2", id: "contact", text: "17. Contact us" },
    {
      type: "p",
      text: "For questions, access, correction or deletion requests, or any matter relating to this privacy policy, you may contact us:",
    },
    { type: "contact" },
  ],
};

/* ------------------------------------------------------------------ */
/* TERMS – Hebrew                                                      */
/* ------------------------------------------------------------------ */

const termsHe: LegalDocument = {
  title: "תנאי שימוש",
  lastUpdated: "עדכון אחרון: יולי 2026",
  tocTitle: "תוכן עניינים",
  metaTitle: "תנאי שימוש | אדם בטיחות",
  metaDescription:
    "תנאי השימוש באתר אדם בטיחות – היקף השירותים, קניין רוחני, הגבלת אחריות, הזמנת שירותים וביטול עסקה בהתאם לדין הישראלי.",
  canonical: "https://adam-safety.com/terms",
  sections: [
    { id: "acceptance", title: "1. קבלת התנאים" },
    { id: "definitions", title: "2. הגדרות" },
    { id: "use", title: "3. השימוש באתר" },
    { id: "content", title: "4. מהות התוכן ואי מתן ייעוץ" },
    { id: "ip", title: "5. קניין רוחני" },
    { id: "services", title: "6. הזמנת שירותים והתקשרות" },
    { id: "cancellation", title: "7. ביטול עסקה" },
    { id: "booking", title: "8. קביעת פגישות ושירותי צד ג'" },
    { id: "liability", title: "9. הגבלת אחריות" },
    { id: "indemnity", title: "10. שיפוי" },
    { id: "availability", title: "11. זמינות האתר" },
    { id: "privacy", title: "12. פרטיות ועוגיות" },
    { id: "accessibility", title: "13. נגישות" },
    { id: "links", title: "14. קישורים חיצוניים" },
    { id: "changes", title: "15. שינויים בתנאים" },
    { id: "law", title: "16. דין וסמכות שיפוט" },
    { id: "contact", title: "17. יצירת קשר" },
  ],
  blocks: [
    {
      type: "lead",
      text: 'ברוכים הבאים לאתר "אדם בטיחות". תנאי שימוש אלה מהווים הסכם משפטי מחייב בינכם לבין החברה, ומסדירים את השימוש באתר ובתכניו. אנא קראו אותם בעיון.',
    },
    { type: "h2", id: "acceptance", text: "1. קבלת התנאים" },
    {
      type: "p",
      text: "עצם הכניסה לאתר, הגלישה בו, מסירת פרטים או שימוש בכל שירות המוצע בו מהווים הסכמה מלאה לתנאים אלה ולמדיניות הפרטיות. אם אינכם מסכימים לתנאי מתנאים אלה, אנא הימנעו משימוש באתר.",
    },
    {
      type: "p",
      text: "התנאים מנוסחים בלשון זכר מטעמי נוחות בלבד ומתייחסים לכל המגדרים. השימוש באתר מותר לבני 18 ומעלה או לגורמים עסקיים הפועלים באמצעות מורשה חתימה.",
    },
    { type: "h2", id: "definitions", text: "2. הגדרות" },
    {
      type: "ul",
      items: [
        { label: "החברה / אנחנו", text: '– "אדם בטיחות", מפעילת האתר ונותנת השירותים.' },
        {
          label: "האתר",
          text: "– אתר האינטרנט בכתובת adam-safety.com על כל עמודיו, תכניו ורכיביו.",
        },
        {
          label: "משתמש / אתם",
          text: "– כל אדם או גוף העושה שימוש באתר, בין כמבקר ובין כלקוח.",
        },
        {
          label: "השירותים",
          text: "– שירותי ניהול בטיחות, ייעוץ, הדרכות, סקרי סיכונים וליווי רגולטורי המוצעים על ידי החברה.",
        },
      ],
    },
    { type: "h2", id: "use", text: "3. השימוש באתר" },
    {
      type: "p",
      text: "האתר מיועד לשימוש אישי ועסקי לגיטימי לצורך קבלת מידע על השירותים ויצירת קשר. חל איסור על:",
    },
    {
      type: "ul",
      items: [
        { text: "שימוש באתר למטרה בלתי חוקית או בניגוד לתנאים אלה" },
        { text: "העתקה, שכפול, הפצה או פרסום של תכני האתר ללא אישור מראש ובכתב" },
        {
          text: "איסוף אוטומטי של מידע (Scraping, Crawling), הפעלת בוטים או כריית תוכן למאגרים או לאימון מודלים, ללא הסכמתנו",
        },
        { text: "ניסיון לחדור למערכות, לשבש את פעילות האתר או להעמיס עליו" },
        { text: "מסירת פרטים כוזבים או התחזות לאדם או לגוף אחר" },
        { text: "העלאה או שליחה של תוכן פוגעני, מטעה או המפר זכויות של צד שלישי" },
      ],
    },
    {
      type: "p",
      text: "אנו רשאים לחסום גישה למשתמש שהפר תנאים אלה, ללא הודעה מוקדמת ומבלי לגרוע מכל סעד אחר.",
    },
    { type: "h2", id: "content", text: "4. מהות התוכן ואי מתן ייעוץ" },
    {
      type: "p",
      text: "התכנים באתר, לרבות מאמרים, מדריכים, מחשבונים והפניות לחקיקה ולתקנות, הם מידע כללי בלבד למטרות אינפורמטיביות. אין בהם משום ייעוץ מקצועי, משפטי או הנדסי פרטני, ואין להסתמך עליהם כתחליף לקבלת ייעוץ פרטני מגורם מוסמך בהתאם לנסיבות הספציפיות.",
    },
    {
      type: "p",
      text: "החקיקה והתקנות בתחום הבטיחות מתעדכנות מעת לעת. אף שאנו עושים מאמץ לעדכן את התכנים, איננו מתחייבים לדיוקם, שלמותם או עדכניותם בכל רגע נתון, והנוסח המחייב הוא נוסח החקיקה הרשמי.",
    },
    { type: "h2", id: "ip", text: "5. קניין רוחני" },
    {
      type: "p",
      text: "כל זכויות הקניין הרוחני באתר – לרבות עיצוב, קוד, טקסטים, מאמרים, לוגו, סימני מסחר, תמונות, גרפיקה ומבנה האתר – שייכות לחברה או לצדדים שלישיים שהעניקו לה רישיון שימוש, ומוגנות לפי חוק זכות יוצרים, התשס\"ח-2007 ולפי כל דין.",
    },
    {
      type: "p",
      text: "אין להעתיק, לשכפל, לתרגם, לשנות, להפיץ, לשדר או לעשות שימוש מסחרי בתכנים ללא הסכמתנו מראש ובכתב. מותר ציטוט הוגן ומוגבל של קטעי תוכן בליווי קרדיט וקישור פעיל לעמוד המקור.",
    },
    { type: "h2", id: "services", text: "6. הזמנת שירותים והתקשרות" },
    {
      type: "p",
      text: "האתר אינו מהווה הצעה מחייבת. פנייה דרך האתר או קביעת פגישה מהוות בקשה לקבלת מידע או הצעת מחיר בלבד. התקשרות מחייבת תיווצר רק לאחר חתימת הסכם או אישור הצעת מחיר בכתב.",
    },
    {
      type: "p",
      text: "תנאי התשלום, היקף השירות, לוחות הזמנים והאחריות המקצועית ייקבעו בהסכם או בהצעת המחיר הספציפיים. במקרה של סתירה בין תנאי שימוש אלה לבין הסכם חתום – יגברו הוראות ההסכם החתום.",
    },
    { type: "h2", id: "cancellation", text: "7. ביטול עסקה" },
    {
      type: "p",
      text: 'ביטול עסקה על ידי צרכן יתאפשר בהתאם להוראות חוק הגנת הצרכן, התשמ"א-1981 ולתקנות שהותקנו מכוחו, לרבות לעניין עסקאות מכר מרחוק והדרכות. הודעת ביטול תימסר בדוא"ל Support@Adam-Safety.com ותכלול את פרטי המזמין ופרטי העסקה.',
    },
    {
      type: "p",
      text: "לעניין הדרכות וסדנאות: ביטול הנעשה במועד סמוך למועד ההדרכה עשוי לחייב בדמי ביטול בהתאם לדין ולתנאי ההזמנה. לקוחות עסקיים יחויבו בהתאם לתנאי ההסכם ביניהם לבין החברה.",
    },
    { type: "h2", id: "booking", text: "8. קביעת פגישות ושירותי צד שלישי" },
    {
      type: "p",
      text: "האתר משלב שירותים של צדדים שלישיים, ובכללם מערכת קביעת פגישות (Cal.com) ותקשורת בוואטסאפ. השימוש בהם כפוף גם לתנאי השימוש ולמדיניות הפרטיות של אותם ספקים. איננו אחראים לתקלות, לזמינות או לשינויים בשירותים אלה.",
    },
    { type: "h2", id: "liability", text: "9. הגבלת אחריות" },
    {
      type: "p",
      text: 'האתר ותכניו מוצעים כמות שהם ("AS IS"). בכפוף לכל דין, החברה, מנהליה ועובדיה לא יישאו באחריות לכל נזק ישיר, עקיף, תוצאתי, מיוחד או אובדן רווח שייגרם כתוצאה משימוש באתר, מהסתמכות על תכניו, מאי-זמינותו או מכל תקלה טכנית.',
    },
    {
      type: "p",
      text: "אין באמור כדי לגרוע מאחריות החברה כלפי לקוחותיה במסגרת הסכמי השירות החתומים עמם, ואין בו כדי לשלול אחריות שלא ניתן להתנות עליה לפי דין.",
    },
    { type: "h2", id: "indemnity", text: "10. שיפוי" },
    {
      type: "p",
      text: "משתמש שיפר תנאים אלה או יעשה באתר שימוש בניגוד לדין, ישפה את החברה בגין כל נזק, הוצאה או תשלום שייגרמו לה כתוצאה מכך, לרבות שכר טרחת עורך דין והוצאות משפט.",
    },
    { type: "h2", id: "availability", text: "11. זמינות האתר" },
    {
      type: "p",
      text: "אנו שואפים לזמינות רציפה, אך רשאים להשבית את האתר או חלקים ממנו לצורכי תחזוקה, שדרוג או מכל סיבה אחרת, ללא הודעה מוקדמת. איננו מתחייבים כי האתר יפעל ללא הפרעות או נקי מתקלות.",
    },
    { type: "h2", id: "privacy", text: "12. פרטיות ועוגיות" },
    {
      type: "p",
      text: "השימוש באתר כפוף גם למדיניות הפרטיות שלנו, המהווה חלק בלתי נפרד מתנאים אלה ומפרטת את אופן איסוף המידע והשימוש בו, לרבות שימוש בעוגיות.",
    },
    { type: "cookieButton", text: "פתיחת הגדרות העוגיות" },
    { type: "h2", id: "accessibility", text: "13. נגישות" },
    {
      type: "p",
      text: 'אנו פועלים להנגשת האתר בהתאם לתקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות), התשע"ג-2013 ולתקן הישראלי 5568. אם נתקלתם בקושי בנגישות, נשמח שתעדכנו אותנו ונפעל לתקן זאת.',
    },
    { type: "h2", id: "links", text: "14. קישורים חיצוניים" },
    {
      type: "p",
      text: "האתר עשוי לכלול קישורים לאתרים חיצוניים. הקישור אינו מהווה המלצה או אישור לתוכן, ואיננו אחראים לתכנים, למוצרים או למדיניות הפרטיות של אתרים אלה.",
    },
    { type: "h2", id: "changes", text: "15. שינויים בתנאים" },
    {
      type: "p",
      text: "אנו רשאים לעדכן תנאים אלה בכל עת. הנוסח המחייב הוא הנוסח המפורסם באתר במועד השימוש, והמשך השימוש לאחר העדכון מהווה הסכמה לתנאים המעודכנים.",
    },
    { type: "h2", id: "law", text: "16. דין וסמכות שיפוט" },
    {
      type: "p",
      text: "על תנאים אלה יחולו דיני מדינת ישראל בלבד. סמכות השיפוט הייחודית נתונה לבתי המשפט המוסמכים במחוז תל אביב-יפו. אם ייקבע כי סעיף כלשהו אינו תקף, יתר הסעיפים יעמדו בתוקפם.",
    },
    { type: "h2", id: "contact", text: "17. יצירת קשר" },
    { type: "p", text: "לשאלות בנוגע לתנאי השימוש ניתן לפנות אלינו:" },
    { type: "contact" },
  ],
};

/* ------------------------------------------------------------------ */
/* TERMS – English                                                     */
/* ------------------------------------------------------------------ */

const termsEn: LegalDocument = {
  title: "Terms of Use",
  lastUpdated: "Last updated: July 2026",
  tocTitle: "Table of contents",
  metaTitle: "Terms of Use | Adam Safety",
  metaDescription:
    "Terms of use for the Adam Safety website – scope of services, intellectual property, limitation of liability, ordering services and cancellation under Israeli law.",
  canonical: "https://adam-safety.com/terms",
  sections: [
    { id: "acceptance", title: "1. Acceptance of terms" },
    { id: "definitions", title: "2. Definitions" },
    { id: "use", title: "3. Use of the site" },
    { id: "content", title: "4. Nature of content – no professional advice" },
    { id: "ip", title: "5. Intellectual property" },
    { id: "services", title: "6. Ordering services" },
    { id: "cancellation", title: "7. Cancellation" },
    { id: "booking", title: "8. Booking and third-party services" },
    { id: "liability", title: "9. Limitation of liability" },
    { id: "indemnity", title: "10. Indemnification" },
    { id: "availability", title: "11. Site availability" },
    { id: "privacy", title: "12. Privacy and cookies" },
    { id: "accessibility", title: "13. Accessibility" },
    { id: "links", title: "14. External links" },
    { id: "changes", title: "15. Changes to these terms" },
    { id: "law", title: "16. Governing law and jurisdiction" },
    { id: "contact", title: "17. Contact us" },
  ],
  blocks: [
    {
      type: "lead",
      text: 'Welcome to the "Adam Safety" website. These terms of use constitute a binding legal agreement between you and the Company and govern your use of the site and its content. Please read them carefully.',
    },
    { type: "h2", id: "acceptance", text: "1. Acceptance of terms" },
    {
      type: "p",
      text: "Accessing the site, browsing it, submitting details or using any service offered on it constitutes full acceptance of these terms and of our privacy policy. If you do not agree to any of these terms, please refrain from using the site.",
    },
    {
      type: "p",
      text: "Use of the site is permitted for individuals aged 18 and over, or for business entities acting through an authorised signatory.",
    },
    { type: "h2", id: "definitions", text: "2. Definitions" },
    {
      type: "ul",
      items: [
        {
          label: "Company / we",
          text: '– "Adam Safety", the operator of the site and provider of the services.',
        },
        {
          label: "Site",
          text: "– the website at adam-safety.com, including all its pages, content and components.",
        },
        {
          label: "User / you",
          text: "– any person or entity using the site, whether as a visitor or as a client.",
        },
        {
          label: "Services",
          text: "– safety management, consulting, training, risk surveys and regulatory support offered by the Company.",
        },
      ],
    },
    { type: "h2", id: "use", text: "3. Use of the site" },
    {
      type: "p",
      text: "The site is intended for legitimate personal and business use, to obtain information about the services and to contact us. The following are prohibited:",
    },
    {
      type: "ul",
      items: [
        { text: "Using the site for any unlawful purpose or contrary to these terms" },
        {
          text: "Copying, reproducing, distributing or publishing site content without prior written approval",
        },
        {
          text: "Automated collection of information (scraping, crawling), operating bots, or mining content for databases or model training without our consent",
        },
        {
          text: "Attempting to breach our systems, disrupt the site's operation or overload it",
        },
        { text: "Providing false details or impersonating any person or entity" },
        {
          text: "Uploading or sending offensive or misleading content, or content infringing third-party rights",
        },
      ],
    },
    {
      type: "p",
      text: "We may block access for any user who breaches these terms, without prior notice and without derogating from any other remedy.",
    },
    { type: "h2", id: "content", text: "4. Nature of content – no professional advice" },
    {
      type: "p",
      text: "The content on the site, including articles, guides, calculators and references to legislation and regulations, is general information for informational purposes only. It does not constitute individual professional, legal or engineering advice and should not be relied upon as a substitute for tailored advice from a qualified professional based on your specific circumstances.",
    },
    {
      type: "p",
      text: "Safety legislation and regulations are updated from time to time. Although we make an effort to keep content current, we do not warrant its accuracy, completeness or timeliness at any given moment; the binding text is the official statutory text.",
    },
    { type: "h2", id: "ip", text: "5. Intellectual property" },
    {
      type: "p",
      text: "All intellectual property rights in the site – including design, code, texts, articles, logo, trademarks, images, graphics and site structure – belong to the Company or to third parties who licensed them to it, and are protected under the Israeli Copyright Law, 5768-2007 and any applicable law.",
    },
    {
      type: "p",
      text: "Content may not be copied, reproduced, translated, modified, distributed, broadcast or used commercially without our prior written consent. Fair and limited quotation is permitted with attribution and an active link to the source page.",
    },
    { type: "h2", id: "services", text: "6. Ordering services" },
    {
      type: "p",
      text: "The site does not constitute a binding offer. Contacting us through the site or booking a meeting is a request for information or a quote only. A binding engagement is created only upon signing an agreement or written approval of a quote.",
    },
    {
      type: "p",
      text: "Payment terms, service scope, timelines and professional liability are determined in the specific agreement or quote. In the event of a conflict between these terms and a signed agreement, the signed agreement prevails.",
    },
    { type: "h2", id: "cancellation", text: "7. Cancellation" },
    {
      type: "p",
      text: "Consumer cancellation is available in accordance with the Israeli Consumer Protection Law, 5741-1981 and the regulations thereunder, including with respect to distance selling transactions and training courses. Cancellation notice should be sent to Support@Adam-Safety.com and include the customer's and transaction details.",
    },
    {
      type: "p",
      text: "For training sessions and workshops: cancellation close to the scheduled date may incur cancellation fees as permitted by law and the order terms. Business clients are charged in accordance with their agreement with the Company.",
    },
    { type: "h2", id: "booking", text: "8. Booking and third-party services" },
    {
      type: "p",
      text: "The site integrates third-party services, including an appointment booking system (Cal.com) and WhatsApp communication. Their use is also subject to those providers' terms and privacy policies. We are not responsible for malfunctions, availability or changes in these services.",
    },
    { type: "h2", id: "liability", text: "9. Limitation of liability" },
    {
      type: "p",
      text: 'The site and its content are provided "AS IS". Subject to applicable law, the Company, its officers and employees shall not be liable for any direct, indirect, consequential or special damage or loss of profit arising from use of the site, reliance on its content, its unavailability or any technical fault.',
    },
    {
      type: "p",
      text: "Nothing herein derogates from the Company's liability to its clients under signed service agreements, nor excludes liability that cannot be limited under law.",
    },
    { type: "h2", id: "indemnity", text: "10. Indemnification" },
    {
      type: "p",
      text: "A user who breaches these terms or uses the site unlawfully shall indemnify the Company for any damage, expense or payment incurred as a result, including legal fees and court costs.",
    },
    { type: "h2", id: "availability", text: "11. Site availability" },
    {
      type: "p",
      text: "We strive for continuous availability but may take the site, or parts of it, offline for maintenance, upgrades or any other reason, without prior notice. We do not warrant uninterrupted or error-free operation.",
    },
    { type: "h2", id: "privacy", text: "12. Privacy and cookies" },
    {
      type: "p",
      text: "Use of the site is also subject to our privacy policy, which forms an integral part of these terms and details how information is collected and used, including the use of cookies.",
    },
    { type: "cookieButton", text: "Open cookie settings" },
    { type: "h2", id: "accessibility", text: "13. Accessibility" },
    {
      type: "p",
      text: "We work to make the site accessible in accordance with the Equal Rights for Persons with Disabilities (Service Accessibility Adjustments) Regulations, 5773-2013 and Israeli Standard 5568. If you encounter an accessibility difficulty, please let us know and we will act to correct it.",
    },
    { type: "h2", id: "links", text: "14. External links" },
    {
      type: "p",
      text: "The site may include links to external websites. A link does not constitute a recommendation or endorsement of content, and we are not responsible for the content, products or privacy practices of such sites.",
    },
    { type: "h2", id: "changes", text: "15. Changes to these terms" },
    {
      type: "p",
      text: "We may update these terms at any time. The binding version is the one published on the site at the time of use, and continued use after an update constitutes acceptance of the updated terms.",
    },
    { type: "h2", id: "law", text: "16. Governing law and jurisdiction" },
    {
      type: "p",
      text: "These terms are governed exclusively by the laws of the State of Israel. Exclusive jurisdiction is granted to the competent courts of the Tel Aviv-Jaffa district. If any provision is found invalid, the remaining provisions shall remain in force.",
    },
    { type: "h2", id: "contact", text: "17. Contact us" },
    { type: "p", text: "For questions regarding these terms of use, you may contact us:" },
    { type: "contact" },
  ],
};

export const legalDocuments = {
  privacy: { he: privacyHe, en: privacyEn },
  terms: { he: termsHe, en: termsEn },
};

export const contactLabels = {
  he: { email: 'דוא"ל:', whatsapp: "וואטסאפ:" },
  en: { email: "Email:", whatsapp: "WhatsApp:" },
};
